// Touch/mouse/stylus signature pad. Captures user strokes on a canvas and
// emits a base64 PNG via `onCapture`. Designed for tablet/phone use at the
// Aufnahme- or Besichtigung-Termin where the client signs directly on the
// agent's device.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eraser, Check } from 'lucide-react';

interface Props {
  onCapture: (base64: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
  height?: number;
}

export function SignaturePad({ onCapture, onCancel, disabled, height = 220 }: Props) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState(false);

  // Handle DPR + responsive width.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setupCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(height * dpr);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        // White background so the captured PNG is opaque.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, rect.width, height);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#0a0a0a';
      }
    };
    setupCanvas();
    const observer = new ResizeObserver(setupCanvas);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [height]);

  const ptFromEvent = (e: PointerEvent | React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    drawing.current = true;
    lastPoint.current = ptFromEvent(e);
    setHasInk(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const pt = ptFromEvent(e);
    if (lastPoint.current) {
      ctx.beginPath();
      ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
      ctx.lineTo(pt.x, pt.y);
      ctx.stroke();
    }
    lastPoint.current = pt;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    drawing.current = false;
    lastPoint.current = null;
    const canvas = canvasRef.current;
    if (canvas?.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!ctx || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, height);
    setHasInk(false);
  };

  const submit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Convert to PNG base64.
    const dataUrl = canvas.toDataURL('image/png');
    onCapture(dataUrl);
  };

  return (
    <div className="space-y-3">
      <div
        className="relative overflow-hidden rounded-lg border-2 border-dashed border-line-strong bg-white"
        style={{ height }}
      >
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          className="h-full w-full touch-none"
          style={{ touchAction: 'none' }}
        />
        {!hasInk && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-ink-3">
            {t('signature.placeholder', 'Hier mit dem Finger oder Stift unterschreiben')}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={clear}
          disabled={!hasInk || disabled}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
        >
          <Eraser className="h-3 w-3" />
          {t('signature.clear', 'Löschen')}
        </button>
        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={disabled}
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
            >
              {t('common.cancel', 'Abbrechen')}
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!hasInk || disabled}
            className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper hover:bg-ink/90 disabled:opacity-50"
          >
            <Check className="h-3 w-3" />
            {t('signature.confirm', 'Bestätigen')}
          </button>
        </div>
      </div>
    </div>
  );
}
